import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import _pick from 'lodash/pick';
import {
  getAllCustomQuestions,
  attachFileToCustomQuestion,
} from '../api/CustomQuestion';
import { Audience } from '../api/CustomQuestion/types/Audience';
import { CustomQuestion } from '../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../api/CustomQuestion/types/Type';
import authAPI, { ConsentType, IVentureRegistrationForm } from '../api/auth';
import { EnrichedVentureResponse } from '../api/enrich';
import foundersAPI from '../api/founders';
import specializationsAPI, { Specialization } from '../api/specializations';
import { createPublicVentureGoals } from '../api/goals';
import { getSettings } from '../api/tenants';
import { TenantId } from '../api/tenants/Tenant';
import { Settings } from '../api/tenants/types/Settings';
import { CreateGoal } from '../api/types/Goal';
import venturesAPI from '../api/ventures';
import { Venture } from '../api/ventures/types/Venture';
import { SnackMessage, Text } from '../components/common';
import {
  Attachment,
  getAttachmentFromFile,
} from '../components/common/attachment-card';
import { FormValues } from '../components/common/venture-wizard';
import { ValidFormValues } from '../components/common/wizard';
import VentureApplicationsWizardForm from '../components/forms/report-venture-applications-wizard';
import { COLORS } from '../theme/variables';
import {
  createAttachmentMetadata,
  extractFilePathFromResponse,
  getContentTypeFromExtension,
  handleSubmitCustomForm,
} from '../utils/custom-questions';
import { uuidv4 } from '../utils/functions';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    height: '100vh',
    overflow: 'auto',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: 0,

    [theme.breakpoints.up('xs')]: {
      width: 'calc(100% - 60px)',
      padding: '50px 30px',
    },

    [theme.breakpoints.up('md')]: {
      width: '100%',
      padding: '50px 0',
    },
  },
  block: {
    width: '700px',
    padding: '32px',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 2px rgba(34, 91, 187, 0.16), 0px 4px 8px rgba(51, 126, 255, 0.04), 0px 8px 16px rgba(51, 126, 255, 0.04)',
    borderRadius: 0,
    boxSizing: 'border-box',

    [theme.breakpoints.up('xs')]: {
      borderRadius: '16px',
    },
  },
  loadingBlock: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingContainer: {
    height: '100vh',
    padding: 0,

    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
  },
  successBlock: {
    width: 300,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    borderRadius: '16px',
  },
  successIcon: {
    fontSize: 30,
    color: COLORS.COLOR_GREEN_BASE,
    marginBottom: 15,
  },
  successText: {
    textAlign: 'center',
  },
  title: {
    marginBottom: 15,
  },
}));

function parseFilterFromURL(search: string): any {
  return _pick(parseQuery(search), ['key']);
}

const formatValuesRequest = (
  values: ValidFormValues,
  tenantId: TenantId,
): IVentureRegistrationForm => {
  return {
    venture: {
      ventureName: values.ventureName,
      url: values.url,
      // @ts-expect-error
      numOfEmployees: values.numOfEmployees
        ? Number(values.numOfEmployees)
        : null,
      isTech: values.isTech === 'true',
      country: values.country,
      state: values.state,
      city: values.city,
      address: values.address,
      zip: values.zip,
      tenantId,
      ventureStage: values.ventureStage || null,
      businessModel: values.businessModel || null,
      description: values.description || null,
      mentorshipNeeds: values.mentorshipNeeds || null,
      matchingNotes: values.matchingNotes || null,
      venturesAdditionalInfo: {
        tenantId,
        howDidYouFind: values.howDidYouFind,
        businessDescription: values.businessDescription,
        comments: values.comments,
        currentProgress: values.currentProgress,
        dedication: values.dedication,
        growthRatio: values.growthRatio,
        isLegallyRegistered: values.legally === 'true',
        marketSize: values.marketSize,
        mentorshipPlans: values.mentorshipPlans,
        obstacles: values.obstacles,
        plans: values.plans,
        reasonToStartNow: values.reasonToStartNow,
        requiredSupport: values.requiredSupport,
        yearPlans: values.yearPlans,
      },
    },
    founderList: values.founders
      // @ts-expect-error
      .filter((founder) => Object.keys(founder).length)
      // @ts-expect-error
      .map((founder) => ({
        tenantId,
        firstName: founder.firstName,
        lastName: founder.lastName,
        email: founder.email,
        linkedInProfile: founder.linkedInProfile,
        phone: founder.phone,
        about: founder.about,
        currentRole: founder.currentRole || null,
        education: founder.education || null,
        isSerialFounder: founder.isSerialFounder === 'true' ? true : null,
        foundersAdditionalInfo: {
          tenantId,
          race: founder.race,
          gender: founder.gender,
          veteran: founder.veteran === 'true',
        },
      })),
    // @ts-expect-error
    ventureSpecializationList: values.specializations.map((id) => ({
      specializationId: id,
    })),
  };
};

function mapVentureEnrichToInitialValues(
  data: EnrichedVentureResponse,
  specializations?: Specialization[],
): Partial<FormValues> {
  const values = {} as Record<string, unknown>;
  values.specializations = [];

  if (data.ventureName) values.ventureName = data.ventureName;
  if (data.url) values.url = data.url;
  if (data.address) values.address = data.address;
  if (data.city) values.city = data.city;
  if (data.state) values.state = data.state;
  if (data.zip) values.zip = data.zip;
  if (data.country) values.country = data.country;
  if (data.ventureStage) values.ventureStage = data.ventureStage;
  if (data.businessModel) values.businessModel = data.businessModel;
  if (data.businessDescription)
    values.businessDescription = data.businessDescription;
  if (data.numOfEmployees) values.numOfEmployees = data.numOfEmployees;
  if (data.isTech) values.isTech = data.isTech;
  if (data.currentProgress) values.currentProgress = data.currentProgress;
  if (data.description) values.description = data.description;
  if (data.mentorshipNeeds) values.mentorshipNeeds = data.mentorshipNeeds;
  if (data.matchingNotes) values.matchingNotes = data.matchingNotes;

  // Match specialization names to IDs
  if (data.specializations?.length && specializations?.length) {
    const nameToId = new Map<string, string>();
    for (const s of specializations) {
      nameToId.set(s.name.toLowerCase(), s.id);
    }
    const matchedIds = data.specializations
      .map((name) => nameToId.get(name.toLowerCase()))
      .filter((id): id is string => id != null);
    if (matchedIds.length) {
      values.specializations = matchedIds;
    }
  }

  if (data.founders && data.founders.length > 0) {
    values.founders = data.founders.map((founder) => {
      const f = { active: true, tempId: uuidv4() } as Record<string, unknown>;
      if (founder.firstName) f.firstName = founder.firstName;
      if (founder.lastName) f.lastName = founder.lastName;
      if (founder.email) {
        f.email = founder.email;
        f.emailConfirm = founder.email;
      }
      if (founder.phone) f.phone = founder.phone;
      if (founder.linkedInProfile) f.linkedInProfile = founder.linkedInProfile;
      if (founder.about) f.about = founder.about;
      if (founder.currentRole) f.currentRole = founder.currentRole;
      if (founder.education) f.education = founder.education;
      if (founder.gender) f.gender = founder.gender;
      if (founder.race) f.race = founder.race;
      return f;
    });
  } else {
    values.founders = [{ firstName: '', active: true, tempId: uuidv4() }];
  }

  return values as unknown as Partial<FormValues>;
}

function VentureApplicationsWizardPage() {
  const classes = useStyles();
  const location = useLocation();
  const [statusForm, setStatusForm] = useState<
    'success' | 'loading' | 'error' | ''
  >('loading');
  const [isLoadingSaveForm, setIsLoadingSaveForm] = useState(false);
  const [consents, setConsents] = useState<ConsentType[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [additionalInfoCustomQuestions, setAdditionalInfoCustomQuestions] =
    useState<CustomQuestion[] | null>(null);
  const [founderCustomQuestions, setFounderCustomQuestions] = useState<
    CustomQuestion[] | null
  >(null);
  const [createdVenture, setCreatedVenture] = useState<Venture | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [noteAttachments, setNoteAttachments] = useState<Attachment[]>([]);
  const [attachments, setAttachments] = useState<Map<string, Attachment>>(
    new Map(),
  );
  const [files, setFiles] = useState<Map<string, File>>(new Map());
  const [founderAttachments, setFounderAttachments] = useState<
    Map<string, Attachment>
  >(new Map());
  const [founderFiles, setFounderFiles] = useState<Map<string, File>>(
    new Map(),
  );
  const [showPrefillModal, setShowPrefillModal] = useState(true);
  const [prefillData, setPrefillData] = useState<Partial<FormValues> | null>(
    null,
  );

  const handlePrefillDismiss = useCallback(() => {
    setShowPrefillModal(false);
  }, []);

  const { key } = useMemo(
    () => parseFilterFromURL(location.search),
    [location.search],
  );

  const getParseTenantId = (value: string): TenantId => {
    const parseData = value.split('|');
    return parseData[0] as TenantId;
  };

  const tenantId = useMemo(() => {
    return getParseTenantId(atob(key));
  }, [key]);

  const handlePrefillComplete = useCallback(
    async (data: EnrichedVentureResponse) => {
      let specializations: Specialization[] = [];
      if (data.specializations?.length) {
        try {
          specializations = await specializationsAPI.getPublicAll(
            tenantId as TenantId,
          );
        } catch (_e) {
          // proceed without specialization matching
        }
      }
      const mapped = mapVentureEnrichToInitialValues(data, specializations);
      setPrefillData(mapped);
      setShowPrefillModal(false);
    },
    [tenantId],
  );

  const loadData = async () => {
    try {
      const [loadedConsents, settings] = await Promise.all([
        authAPI.getFounderConsents(tenantId),
        getSettings(tenantId as TenantId),
      ]);

      if (settings?.customQuestionsVentureAdditionalInfoEnabled) {
        const customQuestions = await getAllCustomQuestions(
          tenantId as TenantId,
          Audience.VENTURE_ADDITIONAL_INFO,
        );
        setAdditionalInfoCustomQuestions(customQuestions);
      }

      if (settings?.customQuestionsFounderAdditionalInfoEnabled) {
        try {
          const founderQuestions = await getAllCustomQuestions(
            tenantId as TenantId,
            Audience.FOUNDER_ADDITIONAL_INFO,
          );
          setFounderCustomQuestions(founderQuestions);
        } catch {
          // Proceed without founder custom questions if loading fails
        }
      }

      setSettings(settings);
      setConsents(loadedConsents);
      setStatusForm('');
    } catch (e) {
      setStatusForm('error');
    }
  };

  const handleSubmit = async (values: ValidFormValues) => {
    try {
      setIsLoadingSaveForm(true);
      const validValid = formatValuesRequest(values, tenantId);

      const venture = await authAPI.ventureRegistration(validValid);
      setCreatedVenture(venture);

      // Create goals if Goals Integration is enabled and goals exist
      if (settings?.goalIntegrationEnabled) {
        const threeMonthGoals = (values as any).threeMonthGoals || [];
        const twelveMonthGoals = (values as any).twelveMonthGoals || [];
        const allGoals = [...threeMonthGoals, ...twelveMonthGoals];

        if (allGoals.length > 0) {
          try {
            const goalsToCreate: CreateGoal[] = allGoals.map((goal: any) => ({
              tenantId,
              ventureId: venture.id,
              title: goal.title,
              description: goal.description,
              startDate: goal.startDate,
              targetDate: goal.targetDate,
              status: 'NOT_STARTED' as const,
              progress: 0,
            }));

            await createPublicVentureGoals(tenantId, venture.id, goalsToCreate);
          } catch (error: any) {
            console.error('Error creating goals:', error);
            enqueueSnackbar('Venture created, but failed to create goals', {
              variant: 'warning',
            });
          }
        }
      }

      if (
        settings?.customQuestionsVentureAdditionalInfoEnabled &&
        additionalInfoCustomQuestions?.length
      ) {
        // Upload attachment files first
        const updatedValues = { ...values };
        const updatedAttachments = new Map(attachments);

        // Process all attachment questions
        for (const question of additionalInfoCustomQuestions) {
          if (question.type === CustomQuestionType.ATTACHMENT) {
            const questionId = question.id;
            const currentValue = values[questionId];

            // Check if file needs to be uploaded (has pending marker or file in Map)
            const file = files.get(questionId);
            const hasPendingMarker =
              typeof currentValue === 'string' &&
              currentValue.startsWith('pending:');

            // Check if currentValue is already a valid JSON string (from previous save)
            const isAlreadyJsonString =
              typeof currentValue === 'string' &&
              currentValue.trim() !== '' &&
              !hasPendingMarker &&
              (currentValue.startsWith('{') || currentValue.startsWith('"'));

            if (file && hasPendingMarker) {
              try {
                // Upload file to server
                const response = await attachFileToCustomQuestion(
                  tenantId as TenantId,
                  questionId,
                  venture.id,
                  file,
                );

                const filePath = extractFilePathFromResponse(response);

                if (!filePath) {
                  throw new Error('Failed to get file path from response');
                }

                // Update attachment with URL
                const attachment = attachments.get(questionId);
                if (attachment) {
                  // Clean up temporary blob URL if exists
                  if (attachment.url && attachment.url.startsWith('blob:')) {
                    URL.revokeObjectURL(attachment.url);
                  }

                  const updatedAttachment = {
                    ...attachment,
                    url: filePath,
                  };
                  updatedAttachments.set(questionId, updatedAttachment);

                  // Create AttachmentMetadata object for backend
                  updatedValues[questionId] = createAttachmentMetadata(
                    filePath,
                    `${attachment.name}.${attachment.extension}`,
                    attachment.size,
                    getContentTypeFromExtension(attachment.extension),
                  );
                }
              } catch (error: any) {
                console.error('Error uploading attachment:', error);
                enqueueSnackbar(
                  `Failed to upload attachment for question: ${question.label}`,
                  {
                    variant: 'error',
                  },
                );
                // Remove pending marker on error
                updatedValues[questionId] = '';
              }
            } else if (isAlreadyJsonString) {
              // Keep existing JSON string value (from previous save)
              updatedValues[questionId] = currentValue;
            } else if (
              !currentValue ||
              currentValue === '' ||
              hasPendingMarker
            ) {
              // No file selected or pending marker without file - clear value
              updatedValues[questionId] = '';
            }
          }
        }

        await handleSubmitCustomForm({
          values: updatedValues,
          questions: additionalInfoCustomQuestions,
          responderId: venture.id,
          tenantId: tenantId as TenantId,
          audience: Audience.VENTURE_ADDITIONAL_INFO,
        });
      }

      // Submit founder custom questions
      if (
        settings?.customQuestionsFounderAdditionalInfoEnabled &&
        founderCustomQuestions?.length
      ) {
        try {
          // Fetch created founders to get their backend IDs
          const createdFounders = await foundersAPI.getPublicFounderNames(
            venture.id,
            tenantId,
          );

          // Match wizard founders to created founders by order
          const wizardFounders = (
            values.founders as unknown as Array<Record<string, unknown>>
          ).filter((founder) => Object.keys(founder).length > 0);

          for (let i = 0; i < wizardFounders.length; i++) {
            const wizardFounder = wizardFounders[i];
            let matchedFounder = createdFounders[i];
            if (!matchedFounder) continue;

            // Verify match by name; fall back to name-based lookup
            const wizardFirstName = wizardFounder.firstName as string;
            const wizardLastName = wizardFounder.lastName as string;
            if (
              matchedFounder.firstName !== wizardFirstName ||
              matchedFounder.lastName !== wizardLastName
            ) {
              const nameMatch = createdFounders.find(
                (f) =>
                  f.firstName === wizardFirstName &&
                  f.lastName === wizardLastName,
              );
              if (nameMatch) {
                matchedFounder = nameMatch;
              } else {
                continue;
              }
            }

            const founderTempId = (wizardFounder.tempId as string) || '';
            if (!founderTempId) continue;

            // Check if this founder has any custom question answers
            const hasAnswers = founderCustomQuestions.some((question) => {
              const fieldKey = `founderCustom_${founderTempId}_${question.id}`;
              const value = values[fieldKey];
              return value && typeof value === 'string' && value.trim() !== '';
            });

            if (!hasAnswers) continue;

            // Build values map for this founder's custom questions
            const founderValues: Record<string, unknown> = {};

            for (const question of founderCustomQuestions) {
              const fieldKey = `founderCustom_${founderTempId}_${question.id}`;
              const fieldValue = values[fieldKey];

              if (question.type === CustomQuestionType.ATTACHMENT) {
                const fileKey = `${founderTempId}_${question.id}`;
                const file = founderFiles.get(fileKey);
                const hasPendingMarker =
                  typeof fieldValue === 'string' &&
                  fieldValue.startsWith('pending:');

                if (file && hasPendingMarker) {
                  try {
                    const response = await attachFileToCustomQuestion(
                      tenantId as TenantId,
                      question.id,
                      matchedFounder.founderId,
                      file,
                    );

                    const filePath = extractFilePathFromResponse(response);
                    if (filePath) {
                      const attachment = founderAttachments.get(fileKey);
                      if (attachment) {
                        if (
                          attachment.url &&
                          attachment.url.startsWith('blob:')
                        ) {
                          URL.revokeObjectURL(attachment.url);
                        }
                        founderValues[question.id] = createAttachmentMetadata(
                          filePath,
                          `${attachment.name}.${attachment.extension}`,
                          attachment.size,
                          getContentTypeFromExtension(attachment.extension),
                        );
                      }
                    }
                  } catch (uploadError: unknown) {
                    enqueueSnackbar(
                      `Failed to upload attachment for founder question: ${question.label}`,
                      { variant: 'error' },
                    );
                    founderValues[question.id] = '';
                  }
                } else {
                  founderValues[question.id] = fieldValue || '';
                }
              } else {
                founderValues[question.id] = fieldValue || '';
              }
            }

            await handleSubmitCustomForm({
              values: founderValues,
              questions: founderCustomQuestions,
              responderId: matchedFounder.founderId,
              tenantId: tenantId as TenantId,
              audience: Audience.FOUNDER_ADDITIONAL_INFO,
            });
          }
        } catch (founderCqError: unknown) {
          enqueueSnackbar(
            'Venture created, but failed to save some founder custom questions',
            { variant: 'warning' },
          );
        }
      }

      // setStatusForm('success');
      setIsLoadingSaveForm(false);
    } catch (e: any) {
      setIsLoadingSaveForm(false);
      const messageError = e.response?.data?.message || 'Internal server error';

      enqueueSnackbar('An error occurred while enrollment', {
        variant: 'error',
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  const handleNoteFileUpload = async (file: File) => {
    if (createdVenture?.id) {
      try {
        const fileIndex = noteAttachments.length;
        setNoteAttachments((prevAttachments) => [
          ...prevAttachments,
          getAttachmentFromFile(file),
        ]);
        const fileURL = await venturesAPI.attachPublicFileToNote(
          tenantId,
          createdVenture.id,
          file,
        );
        setNoteAttachments((prevAttachments) => {
          return prevAttachments.map((prevAttach, prevAttachIndex) => {
            if (prevAttachIndex !== fileIndex) {
              return prevAttach;
            }
            return {
              ...prevAttach,
              url: fileURL,
            };
          });
        });
      } catch (e: any) {
        console.error(e);
      }
    }
  };

  const handleNoteFileRemove = (index: number) => {
    setNoteAttachments((prevAttachments) =>
      prevAttachments.filter((_, attachIndex) => attachIndex !== index),
    );
  };

  const formatNoteAttachments = (attachments: Attachment[]) => {
    try {
      if (!attachments.length) {
        return '';
      }
      const attachmentRefs = JSON.stringify(attachments);
      return attachmentRefs;
    } catch (e: any) {
      return '';
    }
  };

  const createNote = async () => {
    if (noteAttachments?.length && createdVenture?.id) {
      const attachmentRefs = formatNoteAttachments(noteAttachments);
      await venturesAPI.createPublicNote(tenantId, createdVenture.id, {
        ventureId: createdVenture.id,
        contents:
          'A deck was provided as a part of venture application process.',
        attachmentRefs: attachmentRefs,
      });
    }
  };

  const showSuccess = useCallback(() => {
    setStatusForm('success');
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (statusForm === 'loading') {
    return (
      <div
        data-testid='report-page'
        className={cn(classes.container, classes.loadingContainer)}
      >
        <div className={cn(classes.block, classes.loadingBlock)}>
          <CircularProgress size={54} color='primary' />
        </div>
      </div>
    );
  }

  if (statusForm === 'success') {
    return (
      <div
        data-testid='venture-wizard-page-success'
        className={cn(classes.container, classes.loadingContainer)}
      >
        <div className={cn(classes.block, classes.successBlock)}>
          <CheckCircleIcon className={classes.successIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>
              Success! Your application has been submitted,
              the Program Administrator will reach out to you separately.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id='wizard-scroll' className={classes.wrapper}>
      <div data-testid='report-page' className={classes.container}>
        <div className={classes.block}>
          <Typography variant='h2' className={classes.title}>
            New Venture Application Form
          </Typography>
          <VentureApplicationsWizardForm
            onSubmit={handleSubmit}
            tenantId={tenantId}
            isLoading={isLoadingSaveForm}
            consents={consents}
            showCustomAdditionalInfo={
              !!settings?.customQuestionsVentureAdditionalInfoEnabled
            }
            additionalInfoCustomQuestions={additionalInfoCustomQuestions}
            intro={settings?.ventureApplicationIntro || ''}
            venture={createdVenture}
            showSuccess={showSuccess}
            noteAttachments={noteAttachments}
            handleNoteFileUpload={handleNoteFileUpload}
            handleNoteFileRemove={handleNoteFileRemove}
            createNote={createNote}
            attachments={attachments}
            setAttachments={setAttachments}
            files={files}
            setFiles={setFiles}
            founderCommitmentEnabled={
              settings?.founderCommitmentEnabled ?? false
            }
            goalIntegrationEnabled={settings?.goalIntegrationEnabled ?? false}
            showFounderCustomQuestions={
              !!settings?.customQuestionsFounderAdditionalInfoEnabled &&
              !!founderCustomQuestions?.length
            }
            founderCustomQuestions={founderCustomQuestions}
            founderAttachments={founderAttachments}
            setFounderAttachments={setFounderAttachments}
            founderFiles={founderFiles}
            setFounderFiles={setFounderFiles}
            showPrefillModal={showPrefillModal}
            onPrefillComplete={handlePrefillComplete}
            onPrefillDismiss={handlePrefillDismiss}
            prefillData={prefillData}
          />
        </div>
      </div>
    </div>
  );
}

export default VentureApplicationsWizardPage;
